import type {
  CatalogCorrelationDto,
  CatalogProfileDetailDto,
  SeniorityLevelDto,
} from '@/api/catalog-client';

// =============================================================================
// catalog-referential-model.ts — Sélecteurs PURS du référentiel des profils (§3.4).
// =============================================================================
// Consultation seule (Uc-CAT-PROFILS, DBU §3.4) : ce module ne porte AUCUNE règle métier
// d'autorité (l'autorité = API, PUBLISHED-only) — uniquement de la dérivation présentationnelle
// à partir du contrat FIGÉ `SupplierCatalogReferentialDto` (profils + corrélations).
//
// Fonctions PURES (sans état, sans I/O) : arborescence familles → sous-familles pour le menu
// latéral gauche, filtrage combiné (famille / sous-famille / niveau / recherche libre), et
// résolution des profils corrélés (chips cliquables). Colocalisé + testé unitairement, ce qui
// isole la logique de tri/filtre du rendu (grille < 150 ms perçu, ENF-01).

/** État de vue porté par l'URL (TanStack search params ; RULE_REF_006 : pas de localStorage). */
export interface CatalogReferentialSearch {
  /** Famille sélectionnée dans le menu latéral (filtre exact). */
  readonly famille?: string;
  /** Sous-famille sélectionnée (affine la famille ; filtre exact). */
  readonly sousFamille?: string;
  /** Niveau d'expertise filtré (le profil doit proposer ce niveau). */
  readonly niveau?: SeniorityLevelDto;
  /** Recherche texte libre (libellé + code JeKo + mots-clés de mission). */
  readonly q?: string;
  /** Code du profil affiché dans la fiche latérale droite (`undefined` = fiche fermée). */
  readonly profil?: string;
}

/** Une sous-famille de l'arborescence (menu latéral) + le nombre de profils qu'elle contient. */
export interface SousFamilleNode {
  readonly key: string;
  readonly count: number;
}

/** Une famille de l'arborescence (menu latéral) avec ses sous-familles et son total de profils. */
export interface FamilleNode {
  readonly key: string;
  readonly count: number;
  readonly sousFamilles: readonly SousFamilleNode[];
}

/** Profil corrélé résolu (chip cliquable) : le profil cible + l'intensité de la corrélation. */
export interface CorrelatedProfile {
  readonly profile: CatalogProfileDetailDto;
  readonly weight: number;
}

/**
 * Normalise une chaîne pour la recherche insensible à la casse ET aux accents (NFD + suppression
 * des diacritiques). « Développeur » et « developpeur » matchent ainsi la même requête.
 */
function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * Construit l'arborescence FAMILLE → SOUS-FAMILLE (menu latéral gauche, DBU §3.4 l.2686/2698)
 * à partir des profils du référentiel. Tri alphabétique stable (fr) à chaque niveau ; les compteurs
 * reflètent le nombre de profils. Aucune mutation des entrées.
 */
export function buildFamilyTree(
  profiles: readonly CatalogProfileDetailDto[],
): readonly FamilleNode[] {
  const familles = new Map<string, Map<string, number>>();
  for (const profile of profiles) {
    const sousFamilles = familles.get(profile.famille) ?? new Map<string, number>();
    sousFamilles.set(profile.sousFamille, (sousFamilles.get(profile.sousFamille) ?? 0) + 1);
    familles.set(profile.famille, sousFamilles);
  }
  return [...familles.entries()]
    .sort(([a], [b]) => a.localeCompare(b, 'fr'))
    .map(([famille, sousFamilles]) => {
      const nodes = [...sousFamilles.entries()]
        .sort(([a], [b]) => a.localeCompare(b, 'fr'))
        .map(([key, count]) => ({ key, count }));
      const count = nodes.reduce((sum, node) => sum + node.count, 0);
      return { key: famille, count, sousFamilles: nodes };
    });
}

/** Le profil satisfait-il la recherche libre (libellé + code JeKo + mots-clés de mission) ? */
function matchesQuery(profile: CatalogProfileDetailDto, query: string): boolean {
  const needle = normalize(query);
  if (needle.length === 0) {
    return true;
  }
  const haystack = [profile.libelle, profile.profileCode, profile.missions ?? '']
    .map(normalize)
    .join(' ');
  return haystack.includes(needle);
}

/**
 * Filtre combiné (DBU §3.4 l.2706-2710) : famille / sous-famille / niveau / recherche libre, tous
 * cumulables. Un filtre absent (`undefined`) n'exclut rien. Le résultat est trié par code JeKo
 * (ordre stable, déterministe) pour un rendu prévisible de la liste.
 */
export function filterProfiles(
  profiles: readonly CatalogProfileDetailDto[],
  search: CatalogReferentialSearch,
): readonly CatalogProfileDetailDto[] {
  return profiles
    .filter((profile) => {
      if (search.famille !== undefined && profile.famille !== search.famille) {
        return false;
      }
      if (
        search.sousFamille !== undefined &&
        profile.sousFamille !== search.sousFamille
      ) {
        return false;
      }
      if (
        search.niveau !== undefined &&
        !profile.availableLevels.includes(search.niveau)
      ) {
        return false;
      }
      if (search.q !== undefined && !matchesQuery(profile, search.q)) {
        return false;
      }
      return true;
    })
    .slice()
    .sort((a, b) => a.profileCode.localeCompare(b.profileCode, 'fr'));
}

/**
 * Résout les profils CORRÉLÉS à un profil donné (chips cliquables, DBU §3.4 l.2724 / navigation §3.4
 * l.2692). La corrélation est orientée (from→to) mais la navigation est bidirectionnelle : on retient
 * toute arête touchant `profileCode` et on résout l'AUTRE extrémité. Profils inconnus (hors référentiel)
 * ignorés ; résultat trié par poids décroissant (les corrélations les plus fortes en tête).
 */
export function correlatedProfiles(
  profileCode: string,
  correlations: readonly CatalogCorrelationDto[],
  byCode: ReadonlyMap<string, CatalogProfileDetailDto>,
): readonly CorrelatedProfile[] {
  const seen = new Set<string>();
  const result: CorrelatedProfile[] = [];
  for (const edge of correlations) {
    let otherCode: string | null = null;
    if (edge.profileFrom === profileCode) {
      otherCode = edge.profileTo;
    } else if (edge.profileTo === profileCode) {
      otherCode = edge.profileFrom;
    }
    if (otherCode === null || seen.has(otherCode)) {
      continue;
    }
    const profile = byCode.get(otherCode);
    if (profile === undefined) {
      continue;
    }
    seen.add(otherCode);
    result.push({ profile, weight: edge.weight });
  }
  return result.sort((a, b) => b.weight - a.weight);
}

/** Index code JeKo → profil (résolution O(1) des chips corrélés + de la fiche sélectionnée). */
export function indexByCode(
  profiles: readonly CatalogProfileDetailDto[],
): ReadonlyMap<string, CatalogProfileDetailDto> {
  return new Map(profiles.map((profile) => [profile.profileCode, profile]));
}
